import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { DailyMetrics, PEDCycle, Phase, CompoundType } from '../../types';
import { metricsService } from '../../services/endpoints/metricsService';
import * as trainingService from '../../services/endpoints/trainingService';
import { useCoachView } from '../../contexts/CoachViewContext';

interface CurrentStatsProps {
  metrics?: DailyMetrics;
  cycle?: PEDCycle;
  phase?: Phase;
  phaseWeek?: number;
}

const CurrentStats: React.FC<CurrentStatsProps> = ({ metrics, cycle, phase, phaseWeek }) => {
  const { viewAs } = useCoachView();

  // ── Anabolic load ──────────────────────────────────────────────────────────
  const anabolicWeeklyLoad = (cycle?.details ?? [])
    .filter(d =>
      d.compound?.type === CompoundType.InjectableAAS ||
      d.compound?.type === CompoundType.OralAAS
    )
    .reduce((sum, d) => sum + (d.weeklyTotal ?? 0), 0);

  const formatLoad = (mg: number): string => {
    if (mg >= 1000) {
      const g = mg / 1000;
      return `${Number.isInteger(g) ? g.toFixed(0) : g.toFixed(1)}g`;
    }
    return `${mg}mg`;
  };

  // ── Phase week — use backend-computed value from countdown.phaseWeek ────────
  const phaseWeekNumber = phaseWeek ?? 0;

  const phaseTypeLabels: Record<string, string> = {
    Cruise: 'Cruise', MiniCut: 'Mini Cut', Hold: 'Hold', Bridge: 'Bridge',
    Rebound: 'Rebound', GrowthBlast: 'Growth Blast',
    ContestPrep: 'Contest Prep', Show: 'Peak Week',
  };
  const phaseLabel = phase?.phaseType
    ? (phaseTypeLabels[phase.phaseType] ?? phase.phaseType).toUpperCase()
    : 'PHASE';

  // ── Last 30 days of metrics (used for steps avg + weight rate) ─────────────
  const { data: recentMetrics = [] } = useQuery({
    queryKey: ['metrics-recent-30d', viewAs],
    queryFn: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 30);
      return metricsService.getMetrics(start.toISOString(), end.toISOString(), undefined, viewAs ?? undefined);
    },
    staleTime: 5 * 60_000,
  });

  // ── Steps — 7-day average ──────────────────────────────────────────────────
  const sevenDayCutoff = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
  const last7Metrics = recentMetrics.filter(m => new Date(m.date) >= sevenDayCutoff);

  // ── Weight — 7-day average ─────────────────────────────────────────────────
  const weightEntries7d = last7Metrics.filter(m => m.weight != null);
  const weightAvg7d = weightEntries7d.length > 0
    ? weightEntries7d.reduce((sum, m) => sum + (m.weight ?? 0), 0) / weightEntries7d.length
    : null;

  const stepsEntries = last7Metrics.filter(m => m.stepsYesterday != null);
  const stepsAvg = stepsEntries.length > 0
    ? Math.round(stepsEntries.reduce((sum, m) => sum + (m.stepsYesterday ?? 0), 0) / stepsEntries.length)
    : null;

  // ── Weight — weekly rate over last 30 days ─────────────────────────────────
  const weightEntries = [...recentMetrics]
    .filter(m => m.weight != null)
    .sort((a, b) => a.date.localeCompare(b.date));

  let weightRatePerWeek: number | null = null;
  if (weightEntries.length >= 2) {
    const earliest = weightEntries[0];
    const latest   = weightEntries[weightEntries.length - 1];
    const daysDiff = (new Date(latest.date).getTime() - new Date(earliest.date).getTime()) / (1000 * 60 * 60 * 24);
    if (daysDiff > 0) {
      const totalChange = (latest.weight ?? 0) - (earliest.weight ?? 0);
      weightRatePerWeek = (totalChange / daysDiff) * 7;
    }
  }

  // ── Training sessions — last 7 days ────────────────────────────────────────
  const { data: sessionHistory = [] } = useQuery({
    queryKey: ['sessions-recent-7d', viewAs],
    queryFn: () => trainingService.getSessionHistory(viewAs ?? undefined, 1, 20),
    staleTime: 5 * 60_000,
  });

  const recentSessionCount = sessionHistory.filter(
    s => new Date(s.completedOn) >= sevenDayCutoff
  ).length;

  // ── Format weight rate ─────────────────────────────────────────────────────
  const weightRateLabel = weightRatePerWeek !== null
    ? `${weightRatePerWeek >= 0 ? '+' : ''}${weightRatePerWeek.toFixed(2)}`
    : '---';
  const weightRateColor = weightRatePerWeek === null
    ? 'text-gold-500'
    : weightRatePerWeek > 0
      ? 'text-green-400'
      : weightRatePerWeek < 0
        ? 'text-red-400'
        : 'text-gold-500';

  const stats = [
    {
      label: 'AVG WEIGHT',
      value: weightAvg7d !== null ? weightAvg7d.toFixed(1) : '---',
      color: 'text-gold-500',
      sub: '7d avg',
    },
    {
      label: 'STEPS',
      value: stepsAvg !== null ? stepsAvg.toLocaleString() : '---',
      color: 'text-gold-500',
      sub: '7d avg',
    },
    {
      label: 'WT CHANGE/WK',
      value: weightRateLabel !== '---' ? `${weightRateLabel} lbs` : '---',
      color: weightRateColor,
      sub: '30d rate',
    },
    {
      label: 'WORKOUTS',
      value: recentSessionCount.toString(),
      color: 'text-gold-500',
      sub: 'last 7 days',
    },
    {
      label: 'ANABOLIC LOAD/WK',
      value: anabolicWeeklyLoad > 0 ? formatLoad(anabolicWeeklyLoad) : '---',
      color: 'text-gold-500',
      sub: null,
    },
    {
      label: phaseLabel,
      value: phaseWeekNumber > 0 ? `Wk ${phaseWeekNumber}` : '---',
      color: 'text-gold-500',
      sub: phase?.durationWeeks ? `of ${phase.durationWeeks} wks` : null,
    },
  ];

  return (
    <div className="py-6 px-8">
      <h2 className="text-sm uppercase tracking-wider text-gray-400 mb-4">
        CURRENT STATS
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {stats.map((stat, index) => (
          <div
            key={index}
            className="stat-card bg-dark-700 rounded-lg p-4 text-center border border-dark-700 hover:border-gold-500/30"
          >
            <div className={`text-2xl font-bold mb-1 ${stat.color}`}>
              {stat.value}
            </div>
            <div className="text-xs uppercase tracking-wider text-gray-400">
              {stat.label}
            </div>
            {stat.sub && (
              <div className="text-xs text-gray-600 mt-0.5 normal-case tracking-normal">
                {stat.sub}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CurrentStats;
